/**
 * SpreadsheetViewerModal Component
 * Displays data in an interactive spreadsheet view within a modal
 */

import React, { useState } from 'react';
import { getFormattedCellValue } from '../services/spreadsheetViewer';
import { SpreadsheetData } from '../services/spreadsheetViewer';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';

interface SpreadsheetViewerModalProps {
  isOpen: boolean;
  data: SpreadsheetData | null;
  onClose: () => void;
  onExport?: () => void;
}

export const SpreadsheetViewerModal: React.FC<SpreadsheetViewerModalProps> = ({
  isOpen,
  data,
  onClose,
  onExport,
}) => {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState('');

  if (!isOpen || !data) return null;

  // Filter rows based on search term
  const filteredRows = data.rows.filter((row) =>
    data.columns.some((col) => {
      const value = String(row[col.key] || '').toLowerCase();
      return value.includes(searchTerm.toLowerCase());
    })
  );

  // Sort rows if needed
  let displayRows = [...filteredRows];
  if (sortColumn) {
    displayRows.sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];

      // Handle numeric values
      const aNum = parseFloat(String(aVal));
      const bNum = parseFloat(String(bVal));

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Handle string values
      const aStr = String(aVal || '');
      const bStr = String(bVal || '');

      if (sortDirection === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  }

  const handleColumnSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      // Toggle sort direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort column
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000,
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          width: '95%',
          maxWidth: '1400px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px',
            borderBottom: '2px solid #e0e0e0',
            backgroundColor: '#f5f5f5',
          }}
        >
          <div>
            <h2 style={{ margin: '0 0 8px 0', color: '#333', fontSize: '20px' }}>
              📊 {data.title}
            </h2>
            <p style={{ margin: '0', color: '#666', fontSize: '12px' }}>
              {displayRows.length} of {data.rows.length} records
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {onExport && (
              <button
                onClick={onExport}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  transition: 'background-color 0.3s',
                }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#45a049')}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#4CAF50')}
                title="Download as Excel"
              >
                <FileDownloadIcon sx={{ fontSize: '16px' }} /> Export
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                padding: '0',
                backgroundColor: '#f0f0f0',
                color: '#333',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                transition: 'background-color 0.3s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#e0e0e0')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
              title="Close"
            >
              <CloseIcon sx={{ fontSize: '20px' }} />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div
          style={{
            padding: '15px 20px',
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#fafafa',
          }}
        >
          <input
            type="text"
            placeholder="🔍 Search across all columns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '6px',
              border: '2px solid #ddd',
              fontSize: '14px',
              transition: 'border-color 0.3s',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = '#2196F3')}
            onBlur={(e) => (e.currentTarget.style.borderColor = '#ddd')}
          />
        </div>

        {/* Table Container */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: 'white',
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '13px',
            }}
          >
            {/* Header Row */}
            <thead>
              <tr style={{ backgroundColor: '#2196F3', position: 'sticky', top: 0 }}>
                <th
                  style={{
                    padding: '12px',
                    textAlign: 'center',
                    color: 'white',
                    fontWeight: '600',
                    borderRight: '1px solid #1976D2',
                    width: '50px',
                    backgroundColor: '#1976D2',
                  }}
                >
                  #
                </th>
                {data.columns.map((col, idx) => (
                  <th
                    key={col.key}
                    onClick={() => handleColumnSort(col.key)}
                    style={{
                      padding: '12px',
                      textAlign: col.align || 'left',
                      color: 'white',
                      fontWeight: '600',
                      borderRight:
                        idx < data.columns.length - 1 ? '1px solid #1976D2' : 'none',
                      cursor: 'pointer',
                      userSelect: 'none',
                      backgroundColor:
                        sortColumn === col.key ? '#0d47a1' : '#1976D2',
                      transition: 'background-color 0.2s',
                      minWidth: '120px',
                    }}
                    onMouseOver={(e) => {
                      if (sortColumn !== col.key) {
                        e.currentTarget.style.backgroundColor = '#1565c0';
                      }
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor =
                        sortColumn === col.key ? '#0d47a1' : '#1976D2';
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {col.header}
                      {sortColumn === col.key && (
                        <span style={{ fontSize: '11px' }}>
                          {sortDirection === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>

            {/* Data Rows */}
            <tbody>
              {displayRows.length > 0 ? (
                displayRows.map((row, rowIdx) => (
                  <tr
                    key={rowIdx}
                    style={{
                      backgroundColor: rowIdx % 2 === 0 ? '#fafafa' : 'white',
                      borderBottom: '1px solid #e0e0e0',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#f0f7ff';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor =
                        rowIdx % 2 === 0 ? '#fafafa' : 'white';
                    }}
                  >
                    <td
                      style={{
                        padding: '10px 12px',
                        textAlign: 'center',
                        color: '#999',
                        borderRight: '1px solid #e0e0e0',
                        fontWeight: '500',
                        fontSize: '12px',
                      }}
                    >
                      {rowIdx + 1}
                    </td>
                    {data.columns.map((col) => (
                      <td
                        key={col.key}
                        style={{
                          padding: '10px 12px',
                          textAlign: col.align || 'left',
                          color: '#333',
                          borderRight: '1px solid #e0e0e0',
                        }}
                      >
                        {getFormattedCellValue(row, col)}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={data.columns.length + 1}
                    style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: '#999',
                      fontSize: '14px',
                      fontStyle: 'italic',
                    }}
                  >
                    No results found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '15px 20px',
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#fafafa',
            fontSize: '12px',
            color: '#666',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            Displaying <strong>{displayRows.length}</strong> of{' '}
            <strong>{data.rows.length}</strong> records
          </div>
          <div style={{ display: 'flex', gap: '20px', fontSize: '11px' }}>
            {searchTerm && (
              <span style={{ color: '#2196F3' }}>
                🔍 Filtered by: <strong>{searchTerm}</strong>
              </span>
            )}
            {sortColumn && (
              <span style={{ color: '#2196F3' }}>
                📊 Sorted by: <strong>{data.columns.find((c) => c.key === sortColumn)?.header}</strong>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpreadsheetViewerModal;
